import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../collapsible/collapsible";
import { DropdownMenuContent } from "../dropdown-menu/dropdown-menu";
import { IconButton } from "../icon-button/icon-button";
import { Icon, type IconName } from "../icons/icon";
import { Kbd, KbdGroup } from "../kbd/kbd";
import { Input } from "../input/input";
import { ScrollArea } from "../scroll-area/scroll-area";
import { Separator } from "../separator/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "../sheet/sheet";
import { Skeleton } from "../skeleton/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip/tooltip";
import { useIsMobile } from "../../hooks/use-mobile";
import { cn } from "../../lib/utils";
import { Slot } from "radix-ui";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContext = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openMobile: boolean;
  setOpenMobile: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

type SidebarProviderProps = React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: SidebarProviderProps) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  // Internal state supports controlled and uncontrolled app-shell usage.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback<
    React.Dispatch<React.SetStateAction<boolean>>
  >(
    (value) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open],
  );

  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      setOpenMobile((open) => !open);
      return;
    }

    setOpen((open) => !open);
  }, [isMobile, setOpen]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContext>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        data-slot="sidebar-provider"
        style={
          {
            "--sidebar-width": SIDEBAR_WIDTH,
            "--sidebar-effective-width": open ? SIDEBAR_WIDTH : "0rem",
            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          "group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

type SidebarProps = React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
};

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: SidebarProps) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "group/sidebar flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground",
          variant === "sidebar" && "[--sidebar:var(--background)]",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className={cn(
            "w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground",
            variant === "sidebar" && "[--sidebar:var(--background)]",
          )}
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
          showCloseButton={false}
        >
          <SheetTitle className="sr-only">Sidebar</SheetTitle>
          <SheetDescription className="sr-only">
            Application navigation
          </SheetDescription>
          <div
            className={cn(
              "group/sidebar flex h-full w-full flex-col bg-sidebar",
              className,
            )}
          >
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      data-slot="sidebar"
      className="group/sidebar group peer relative hidden text-sidebar-foreground md:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
    >
      <div
        data-slot="sidebar-gap"
        className={cn(
          "relative h-full w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          variant === "floating"
            ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
        )}
      />
      <div
        data-slot="sidebar-panel"
        className={cn(
          "absolute inset-y-0 z-10 hidden h-full w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex",
          side === "left"
            ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          variant === "floating"
            ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
          variant === "sidebar" && [
            "[--sidebar:var(--background)]",
            side === "left"
              ? "border-r border-sidebar-border"
              : "border-l border-sidebar-border",
          ],
          className,
        )}
        {...props}
      >
        <div
          data-slot="sidebar-body"
          data-sidebar="sidebar"
          className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow-xs"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

type SidebarTriggerProps = Omit<
  React.ComponentProps<typeof IconButton>,
  "icon" | "tooltipContent"
> & {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  showOnHover?: boolean;
};

function SidebarTrigger({
  className,
  onClick,
  showOnHover = false,
  ...props
}: SidebarTriggerProps) {
  const { toggleSidebar } = useSidebar();

  return (
    <IconButton
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      size="sm"
      icon="PanelLeft"
      tooltipContent="Toggle Sidebar"
      className={cn(
        showOnHover &&
          "[@media(hover:hover)_and_(pointer:fine)]:group-[:not(:hover):not(:focus-within)]/sidebar:opacity-0",
        className,
      )}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    />
  );
}

function SidebarRail({ className, ...props }: React.ComponentProps<"button">) {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      data-slot="sidebar-rail"
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full hover:group-data-[collapsible=offcanvas]:bg-sidebar",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className,
      )}
      {...props}
    />
  );
}

function SidebarInset({ className, ...props }: React.ComponentProps<"main">) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        "relative flex h-svh min-h-0 min-w-0 flex-1 flex-col bg-sidebar",
        "md:peer-data-[variant=inset]:py-2 md:peer-data-[variant=inset]:pr-2 md:peer-data-[variant=inset]:pl-0 md:peer-data-[variant=inset]:peer-data-[state=collapsed]:pl-2",
        className,
      )}
      {...props}
    />
  );
}

function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn(
        "h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        className,
      )}
      {...props}
    />
  );
}

function SidebarHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn(
        "flex flex-col gap-2 bg-sidebar px-2.5 pt-3 pb-2",
        className,
      )}
      {...props}
    />
  );
}

function SidebarHeaderRow({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-header-row"
      className={cn(
        "flex w-full items-center justify-between px-0.5",
        className,
      )}
      {...props}
    />
  );
}

function SidebarFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 px-2 py-1", className)}
      {...props}
    />
  );
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn("flex w-full shrink-0 px-4", className)}
      {...props}
    >
      <Separator className="bg-sidebar-border" />
    </div>
  );
}

function SidebarContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-hidden group-data-[collapsible=icon]:overflow-hidden",
        className,
      )}
      {...props}
    />
  );
}

function SidebarSeparatedContent({
  className,
  ...props
}: React.ComponentProps<typeof SidebarContent>) {
  return (
    <>
      <SidebarSeparator />
      <SidebarContent className={cn("pt-2", className)} {...props} />
    </>
  );
}

function SidebarScrollArea({
  className,
  children,
  ...props
}: React.ComponentProps<typeof ScrollArea>) {
  return (
    <div
      data-slot="sidebar-scroll-area-wrapper"
      className="relative min-h-0 flex-1 group-data-[collapsible=icon]:hidden"
    >
      <ScrollArea
        data-slot="sidebar-scroll-area"
        data-sidebar="scroll-area"
        className={cn(
          "h-full [&_[data-slot=scroll-area-scrollbar]]:opacity-0 [&_[data-slot=scroll-area-scrollbar]]:transition-opacity focus-within:[&_[data-slot=scroll-area-scrollbar]]:opacity-100 hover:[&_[data-slot=scroll-area-scrollbar]]:opacity-100 [&_[data-slot=scroll-area-viewport]>div]:!block [&_[data-slot=scroll-area-viewport]>div]:!w-full [&_[data-slot=scroll-area-viewport]>div]:!min-w-0",
          className,
        )}
        {...props}
      >
        {children}
      </ScrollArea>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-sidebar to-sidebar/0" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-sidebar to-sidebar/0" />
    </div>
  );
}

function SidebarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn(
        "group/sidebar-group relative flex w-full min-w-0 flex-col px-2 py-1",
        className,
      )}
      {...props}
    />
  );
}

function SidebarFooterGroup({
  className,
  ...props
}: React.ComponentProps<typeof SidebarGroup>) {
  return <SidebarGroup className={cn("mt-auto", className)} {...props} />;
}

function SidebarGroupLabel({
  className,
  asChild = false,
  children,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) {
  const classNames = cn(
    "peer/sidebar-group-label flex h-8 max-w-full min-w-0 shrink-0 items-center justify-start overflow-hidden rounded-md px-2 text-left text-xs font-medium text-muted-foreground ring-sidebar-ring outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 data-[hover=true]:bg-sidebar-accent [&>svg]:size-4 [&>svg]:shrink-0",
    "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
    className,
  );

  if (asChild) {
    return (
      <Slot.Root
        data-slot="sidebar-group-label"
        data-sidebar="group-label"
        className={classNames}
        {...props}
      >
        {children}
      </Slot.Root>
    );
  }

  return (
    <div
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={classNames}
      {...props}
    >
      <span className="min-w-0 truncate">{children}</span>
    </div>
  );
}

function SidebarGroupCollapsibleLabel({
  className,
  children,
  ...props
}: React.ComponentProps<"button">) {
  return (
    <button
      type="button"
      data-slot="sidebar-group-collapsible-label"
      data-sidebar="group-label"
      className={cn(
        "peer/sidebar-group-label group/sidebar-group-collapsible-label flex h-8 w-full shrink-0 items-center justify-start gap-1 rounded-md px-2 text-left text-xs font-medium text-muted-foreground ring-sidebar-ring outline-hidden transition-[margin,opacity,background-color] duration-200 ease-linear group-has-data-[sidebar=group-action]/sidebar-group:pr-8 group-has-[[data-slot=sidebar-group-actions]]/sidebar-group:pr-14 focus-visible:ring-2 data-[hover=true]:bg-sidebar-accent",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className,
      )}
      {...props}
    >
      <span className="min-w-0 truncate">{children}</span>
      <Icon
        src="ChevronDown"
        className="size-4 shrink-0 transition-transform group-data-[state=closed]/sidebar-group-collapsible-label:-rotate-90"
      />
    </button>
  );
}

function SidebarGroupAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        "absolute top-2.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-muted-foreground opacity-70 ring-sidebar-ring outline-hidden transition-[opacity,background-color,color] hover:bg-sidebar-accent hover:text-muted-foreground hover:opacity-100 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "after:absolute after:-inset-2 md:after:hidden",
        "group-data-[collapsible=icon]:hidden",
        "peer-data-[hover=true]/sidebar-group-label:bg-sidebar-accent peer-data-[hover=true]/sidebar-group-label:opacity-100",
        showOnHover &&
          "focus-visible:opacity-100! data-[state=open]:opacity-100! [@media(hover:hover)_and_(pointer:fine)]:group-[:not(:hover):not(:focus-within)]/sidebar-group:opacity-0",
        className,
      )}
      {...props}
    />
  );
}

function SidebarGroupActions({
  className,
  showOnHover = false,
  ...props
}: React.ComponentProps<"div"> & {
  showOnHover?: boolean;
}) {
  return (
    <div
      data-slot="sidebar-group-actions"
      data-sidebar="group-action"
      className={cn(
        "absolute top-2.5 right-3 flex items-center gap-0.5 text-muted-foreground opacity-70 ring-sidebar-ring outline-hidden transition-[opacity,background-color,color] group-data-[collapsible=icon]:hidden peer-data-[hover=true]/sidebar-group-label:opacity-100 focus-within:opacity-100 hover:opacity-100 [&_[data-slot=icon-button]]:size-5 [&_[data-slot=icon-button]]:rounded-md [&_[data-slot=icon-button]]:p-0 [&_[data-slot=icon-button]]:text-muted-foreground [&_[data-slot=icon-button]]:shadow-none [&_[data-slot=icon-button]]:hover:bg-sidebar-accent [&_[data-slot=icon-button]]:hover:text-muted-foreground [&_[data-slot=icon-button]]:focus-visible:ring-2 [&_[data-slot=icon-button]]:focus-visible:ring-sidebar-ring [&_[data-slot=icon-button]]:data-[hover]:bg-sidebar-accent [&_[data-slot=icon-button]]:data-[hover]:text-muted-foreground [&_[data-slot=icon-button]_svg]:size-4 [&_[data-slot=icon-button]_svg]:shrink-0",
        showOnHover &&
          "focus-within:opacity-100! [@media(hover:hover)_and_(pointer:fine)]:group-[:not(:hover):not(:focus-within)]/sidebar-group:opacity-0",
        className,
      )}
      {...props}
    />
  );
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn("w-full min-w-0 text-sm", className)}
      {...props}
    />
  );
}

function SidebarCollapsibleGroup({
  title,
  action,
  children,
  defaultOpen = true,
  labelHovered = false,
  labelProps,
  className,
  ...props
}: Omit<React.ComponentProps<typeof SidebarGroup>, "title"> & {
  title: React.ReactNode;
  action?: React.ReactNode;
  defaultOpen?: boolean;
  labelHovered?: boolean;
  labelProps?: React.ComponentProps<typeof SidebarGroupCollapsibleLabel>;
}) {
  return (
    <Collapsible asChild defaultOpen={defaultOpen}>
      <SidebarGroup
        className={cn("group-data-[collapsible=icon]:hidden", className)}
        {...props}
      >
        <CollapsibleTrigger asChild>
          <SidebarGroupCollapsibleLabel
            data-hover={labelHovered ? "true" : undefined}
            {...labelProps}
          >
            {title}
          </SidebarGroupCollapsibleLabel>
        </CollapsibleTrigger>
        {action}
        <CollapsibleContent>
          <SidebarGroupContent>{children}</SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}

function SidebarMenu({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn("flex w-full min-w-0 flex-col gap-1", className)}
      {...props}
    />
  );
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn("group/menu-item relative min-w-0", className)}
      {...props}
    />
  );
}

const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-has-data-[sidebar=menu-badge]/menu-item:pr-11 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:min-w-0 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline:
          "bg-background shadow-[0_0_0_1px_var(--sidebar-border)] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_var(--sidebar-accent)]",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type SidebarMenuButtonProps = React.ComponentProps<"button"> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>;

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  ...props
}: SidebarMenuButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  const tooltipProps =
    typeof tooltip === "string"
      ? {
          children: tooltip,
        }
      : tooltip;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltipProps}
      />
    </Tooltip>
  );
}

function SidebarMenuButtonContent({
  icon,
  children,
}: {
  icon: IconName;
  children: React.ReactNode;
}) {
  return (
    <>
      <Icon src={icon} />
      <span>{children}</span>
    </>
  );
}

function SidebarMenuSubButtonContent({
  icon,
  iconClassName,
  children,
}: {
  icon: IconName;
  iconClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <span className="flex size-4 shrink-0 items-center justify-center text-sidebar-accent-foreground">
        <Icon src={icon} className={cn("size-4", iconClassName)} />
      </span>
      <span>{children}</span>
    </>
  );
}

function SidebarHeaderMenuButton({
  className,
  emphasis = false,
  ...props
}: SidebarMenuButtonProps & { emphasis?: boolean }) {
  return (
    <SidebarMenuButton
      className={cn(
        "h-8 w-auto gap-2 px-1",
        emphasis && "font-semibold",
        className,
      )}
      {...props}
    />
  );
}

function SidebarHeaderMenu({
  className,
  ...props
}: React.ComponentProps<typeof SidebarMenu>) {
  return <SidebarMenu className={cn("min-w-0 flex-1", className)} {...props} />;
}

function SidebarMenuDropdownButton({
  className,
  ...props
}: SidebarMenuButtonProps) {
  return (
    <SidebarMenuButton
      className={cn(
        "data-[state=open]:bg-sidebar-accent data-[state=open]:font-medium data-[state=open]:text-sidebar-accent-foreground",
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuSearchButton({
  className,
  children = "Search",
  ...props
}: React.ComponentProps<"button">) {
  return (
    <SidebarMenuButton
      type="button"
      data-slot="sidebar-menu-search-button"
      className={cn(
        "h-8 bg-background text-muted-foreground shadow-xs group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:shadow-none hover:bg-background hover:text-foreground focus-visible:bg-background",
        className,
      )}
      tooltip={typeof children === "string" ? children : undefined}
      {...props}
    >
      <Icon src="Search" />
      <span className="truncate">{children}</span>
      <KbdGroup className="ml-auto gap-1 group-data-[collapsible=icon]:hidden">
        <Kbd className="h-5 min-w-5 rounded-md px-1">⌘</Kbd>
        <Kbd className="h-5 min-w-5 rounded-md px-1">S</Kbd>
      </KbdGroup>
    </SidebarMenuButton>
  );
}

function SidebarSearchMenuItem({
  className,
  ...props
}: React.ComponentProps<typeof SidebarMenuItem>) {
  return (
    <SidebarMenuItem className={cn("mb-2 px-1.5", className)} {...props} />
  );
}

function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        "absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground opacity-70 ring-sidebar-ring outline-hidden transition-[opacity,background-color,color] peer-hover/menu-button:text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:opacity-100 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "after:absolute after:-inset-2 md:after:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground data-[state=open]:opacity-100 md:opacity-0",
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuFolderAction({
  className,
  asChild = false,
  showOnHover = true,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="sidebar-menu-folder-action"
      data-sidebar="menu-action"
      className={cn(
        "absolute top-0.5 right-0.5 flex size-7 items-center justify-center rounded-md p-0 text-sidebar-foreground opacity-70 ring-sidebar-ring outline-hidden transition-[opacity,background-color,color] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:opacity-100 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "after:absolute after:-inset-2 md:after:hidden",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0",
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuActionsContent({
  className,
  isMobile,
  ...props
}: React.ComponentProps<typeof DropdownMenuContent> & { isMobile: boolean }) {
  return (
    <DropdownMenuContent
      className={cn("w-56 rounded-lg", className)}
      side={isMobile ? "bottom" : "right"}
      align={isMobile ? "end" : "start"}
      {...props}
    />
  );
}

function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        "pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-secondary px-1 text-xs font-semibold text-secondary-foreground tabular-nums select-none",
        "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean;
}) {
  const width = React.useMemo(
    () => `${Math.floor(Math.random() * 40) + 50}%`,
    [],
  );

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn("flex h-8 items-center gap-2 rounded-md px-2", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

function SidebarMenuFolderButton({
  children,
  className,
  icon,
  iconClassName,
  iconContainerClassName,
  ...props
}: Omit<React.ComponentProps<typeof SidebarMenuButton>, "children"> & {
  children: React.ReactNode;
  icon?: IconName;
  iconClassName?: string;
  iconContainerClassName?: string;
}) {
  return (
    <SidebarMenuButton
      className={cn(
        icon && "py-1 pr-2 pl-1 group-data-[collapsible=icon]:p-1!",
        className,
      )}
      {...props}
    >
      {icon ? (
        <span
          className={cn(
            "flex size-6 shrink-0 items-center justify-center rounded-md",
            iconContainerClassName,
          )}
        >
          <Icon src={icon} className={cn("size-4", iconClassName)} />
        </span>
      ) : (
        <>
          <Icon
            src="Folder"
            className="group-data-[state=open]/collapsible:hidden"
          />
          <Icon
            src="FolderOpen"
            className="hidden group-data-[state=open]/collapsible:block"
          />
        </>
      )}
      <span className="flex min-w-0 items-center">
        <span className="min-w-0 truncate">{children}</span>
        <Icon
          src="ChevronRight"
          className="ml-1 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/collapsible:rotate-90"
        />
      </span>
    </SidebarMenuButton>
  );
}

function SidebarMenuFolder({
  defaultOpen = true,
  className,
  ...props
}: React.ComponentProps<typeof Collapsible>) {
  return (
    <Collapsible
      asChild
      defaultOpen={defaultOpen}
      className={cn("group/collapsible", className)}
      {...props}
    />
  );
}

function SidebarMenuFolderTrigger({
  ...props
}: React.ComponentProps<typeof CollapsibleTrigger>) {
  return <CollapsibleTrigger asChild {...props} />;
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        "flex min-w-0 flex-col gap-1 px-1.5 py-0.5",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuFolderContent({
  children,
  ...props
}: React.ComponentProps<typeof SidebarMenuSub>) {
  return (
    <CollapsibleContent>
      <SidebarMenuSub {...props}>{children}</SidebarMenuSub>
    </CollapsibleContent>
  );
}

function SidebarMenuEmpty({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <SidebarMenuSubItem>
      <span
        className={cn(
          "block px-2 py-1 text-sm text-muted-foreground",
          className,
        )}
        {...props}
      />
    </SidebarMenuSubItem>
  );
}

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn("group/menu-sub-item relative min-w-0", className)}
      {...props}
    />
  );
}

function SidebarMenuSubAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="sidebar-menu-sub-action"
      data-sidebar="menu-sub-action"
      className={cn(
        "absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground opacity-70 ring-sidebar-ring outline-hidden transition-[opacity,background-color,color] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:opacity-100 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "after:absolute after:-inset-2 md:after:hidden",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "group-focus-within/menu-sub-item:opacity-100 group-hover/menu-sub-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0",
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuSubButton({
  asChild = false,
  size = "md",
  isActive,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean;
  size?: "sm" | "md";
  isActive?: boolean;
}) {
  const Comp = asChild ? Slot.Root : "a";

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "flex h-8 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground ring-sidebar-ring outline-hidden group-has-data-[sidebar=menu-sub-action]/menu-sub-item:pr-8 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:min-w-0 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...props}
    />
  );
}

export {
  Sidebar,
  SidebarCollapsibleGroup,
  SidebarContent,
  SidebarFooter,
  SidebarFooterGroup,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupActions,
  SidebarGroupCollapsibleLabel,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarHeaderMenu,
  SidebarHeaderMenuButton,
  SidebarHeaderRow,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuActionsContent,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuButtonContent,
  SidebarMenuDropdownButton,
  SidebarMenuEmpty,
  SidebarMenuFolder,
  SidebarMenuFolderAction,
  SidebarMenuFolderButton,
  SidebarMenuFolderContent,
  SidebarMenuFolderTrigger,
  SidebarMenuItem,
  SidebarMenuSearchButton,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubAction,
  SidebarMenuSubButton,
  SidebarMenuSubButtonContent,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarScrollArea,
  SidebarSearchMenuItem,
  SidebarSeparatedContent,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
